import React, { useEffect, useState } from 'react';
import {
  visionOptions,
  hearingOptions,
  physicalOptions,
  cognitiveOptions,
} from './models/AccessibilityPreferences';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Card from '@mui/material/Card';
import TopNavActions from './TopNavActions';
import { useTranslation } from 'react-i18next';
import ConfirmExitDialog from './ConfirmExitDialog';
import { Button } from '@mui/material';

interface AccessibilitySettingsEnrollmentViewProps {
  enrollment: any;
  onPageChange: (page: string) => void;
}

const options = [
  { name: 'no', displayName: 'No, the current settings are fine' },
  { name: 'yes', displayName: 'Yes, I would like to use some of the accessibility features of the app' },
];

const AccessibilitySettingsEnrollmentView: React.FC<AccessibilitySettingsEnrollmentViewProps> = ({
  enrollment,
  onPageChange,
}) => {
  const [selectedQuestion1, setSelectedQuestion1] = useState<string[]>(['no']);
  const [selectedQuestion2, setSelectedQuestion2] = useState<string[]>(['no']);
  const [selectedQuestion3, setSelectedQuestion3] = useState<string[]>(['no']);
  const [selectedQuestion4, setSelectedQuestion4] = useState<string[]>(['no']);
  const [vision, setVision] = useState<string[]>([]);
  const [hearing, setHearing] = useState<string[]>([]);
  const [physical, setPhysical] = useState<string[]>([]);
  const [cognitive, setCognitive] = useState<string[]>([]);
  const { t } = useTranslation('enrollment');

  useEffect(() => {
    setVision(enrollment?.accessibilityPreferences?.vision?.length > 0 ? enrollment.accessibilityPreferences.vision : []);
    setHearing(enrollment?.accessibilityPreferences?.hearing?.length > 0 ? enrollment.accessibilityPreferences.hearing : []);
    setPhysical(enrollment?.accessibilityPreferences?.physical?.length > 0 ? enrollment.accessibilityPreferences.physical : []);
    setCognitive(enrollment?.accessibilityPreferences?.cognitive?.length > 0 ? enrollment.accessibilityPreferences.cognitive : []);
    setSelectedQuestion1(enrollment?.accessibilityPreferences?.vision?.length > 0 ? ['yes'] : ['no']);
    setSelectedQuestion2(enrollment?.accessibilityPreferences?.hearing?.length > 0 ? ['yes'] : ['no']);
    setSelectedQuestion3(enrollment?.accessibilityPreferences?.physical?.length > 0 ? ['yes'] : ['no']);
    setSelectedQuestion4(enrollment?.accessibilityPreferences?.cognitive?.length > 0 ? ['yes'] : ['no']);
    // eslint-disable-next-line
  }, [enrollment]);

  const checkNoQuestion = (question: number, selected: string[]) => {
    if (question === 1 && selected.includes('no')) setVision([]);
    if (question === 2 && selected.includes('no')) setHearing([]);
    if (question === 3 && selected.includes('no')) setPhysical([]);
    if (question === 4 && selected.includes('no')) setCognitive([]);
  };

  const [openExitDialog, setOpenExitDialog] = React.useState(false);

  const handleNext = () => {
    enrollment.accessibilityPreferences = {
      vision,
      hearing,
      physical,
      cognitive,
    };
    onPageChange('about-this-study-view');
  };

  const handleBack = () => {
    onPageChange('back');
  };

  const isDisabled =
    (selectedQuestion1.includes('yes') && vision.length === 0) ||
    (selectedQuestion2.includes('yes') && hearing.length === 0) ||
    (selectedQuestion3.includes('yes') && physical.length === 0) ||
    (selectedQuestion4.includes('yes') && cognitive.length === 0);

  // Helper for rendering options
  const renderOptions = (
    label: string,
    selected: string[],
    setSelected: (v: string[]) => void,
    questionNum: number
  ) => (
      <div>
        <FormControl component="fieldset">
          <FormLabel component="legend">
              <Typography variant="h6" component="div">
              {label}
            </Typography>
          </FormLabel>
          <RadioGroup
            name={`question${questionNum}`}
            value={selected[0] || ''}
            onChange={e => {
              setSelected([e.target.value]);
              checkNoQuestion(questionNum, [e.target.value]);
            }}
          >
            {options.map(opt => (
              <FormControlLabel
                key={opt.name}
                value={opt.name}
                control={<Radio />}
                label={t(opt.displayName)}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </div>
  );

  const renderFeatureSelection = (
    show: boolean,
    optionsList: { name: string; displayName: string }[],
    selected: string[],
    setSelected: (v: string[]) => void
  ) =>
    show && (
      <FormControl component="fieldset" fullWidth>
        <FormGroup>
          {optionsList.map(opt => (
            <FormControlLabel
              key={opt.name}
              control={
                <Checkbox
                  checked={selected.includes(opt.name)}
                  onChange={() => {
                    if (selected.includes(opt.name)) {
                      setSelected(selected.filter(x => x !== opt.name));
                    } else {
                      setSelected([...selected, opt.name]);
                    }
                  }}
                />
              }
              label={t(opt.displayName)}
            />
          ))}
        </FormGroup>
      </FormControl>
    );

  return (
    <div>
      <TopNavActions
        onBack={handleBack}
        onExit={() => setOpenExitDialog(true)}
      />
      <ConfirmExitDialog
        open={openExitDialog}
        onClose={() => setOpenExitDialog(false)}
        onExit={() => { window.location.href = '/'; }}
      />

    <Typography variant="h4" id="page-heading" component='h1'>{t("Let's customize your experience")}</Typography>
      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        {renderOptions(
          t("This application requires you to read text to answer questions. Do you have any vision-related requirements we can assist with?"),
          selectedQuestion1,
          setSelectedQuestion1,
          1
        )}
        {renderFeatureSelection(
          selectedQuestion1.includes('yes'),
          visionOptions,
          vision,
          setVision
        )}
      </Card>
      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        {renderOptions(
          t("This application may present some sound files for you to listen to. Do you have any hearing-related requirements we can assist with?"),
          selectedQuestion2,
          setSelectedQuestion2,
          2
        )}
        {renderFeatureSelection(
          selectedQuestion2.includes('yes'),
          hearingOptions,
          hearing,
          setHearing
        )}
      </Card>
      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        {renderOptions(
          t("This application requires you to navigate through the questions using a touch screen. Do you have any physical challenges we can accommodate?"),
          selectedQuestion3,
          setSelectedQuestion3,
          3
        )}
        {renderFeatureSelection(
          selectedQuestion3.includes('yes'),
          physicalOptions,
          physical,
          setPhysical
        )}
      </Card>

      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        {renderOptions(
          t("This application requires you to read text, answer questions, and follow directions. Do you have any cognitive challenges, including difficulty reading, that we can assist with?"),
          selectedQuestion4,
          setSelectedQuestion4,
          4
        )}
        {renderFeatureSelection(
          selectedQuestion4.includes('yes'),
          cognitiveOptions,
          cognitive,
          setCognitive
        )}
      </Card>

      <Button variant="contained" onClick={handleNext} disabled={isDisabled} style={{ marginBottom: 32 }}>
        {t("Next")}
      </Button>
    </div>
  );
};

export default AccessibilitySettingsEnrollmentView;
